// Core
import React, {
  useEffect,
  useMemo,
} from 'react';

// Libraries
import { Progress as ProgressAntD } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint';

// Component
import {
  Container,
  ContentContainer,
  DataValue,
  GradientDefinitions,
  MaxValueContainer,
  MaxValueNumber,
  MaxValueTick,
  MinValueContainer,
  MinValueNumber,
  MinValueTick,
  ProgressSpeedometer,
  TitleContainer,
  UnitType,
} from './Speedometer.style';
import { SpeedometerProps } from './Speedometer.types';

// Types
import { Theme } from '../../../types/theme';

// Plugins
import {
  currentValuePercentage,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
} from '../../../plugins/general';

// Layout
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { ThresholdErrorTooltip } from '../ThresholdErrorTooltip';

function Speedometer({
  className,
  decimalPlaces = 0,
  maxValue,
  minValue,
  running = true,
  testId,
  thresholds,
  title,
  unit,
  value,
}: SpeedometerProps): JSX.Element {
  // Dependencies
  const { i18n } = useTranslation();
  const { isXs, isMd, isSm } = useBreakpoint();
  const theme: Theme = useTheme();

  // Validates thresholds and returns error message if invalid
  const thresholdValidationError = useMemo((): string | null => {
    if (!thresholds) return null;

    const fieldName = title ? `[${title}] ` : '';

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.lowWarning)
      && thresholds.lowAlarm >= thresholds.lowWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [lowWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, lowWarning: ${thresholds.lowWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowAlarm >= thresholds.highWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [highWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowAlarm >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowAlarm] must be less than [highAlarm] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowWarning >= thresholds.highWarning
    ) {
      return `${fieldName}[lowWarning] must be less than [highWarning] `
        + `(lowWarning: ${thresholds.lowWarning}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowWarning] must be less than [highAlarm] `
        + `(lowWarning: ${thresholds.lowWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.highWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.highWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[highWarning] must be less than [highAlarm] `
        + `(highWarning: ${thresholds.highWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    return null;
  }, [thresholds, title]);

  // Determines the progress size based on the current breakpoint
  const progressSize = useMemo((): number => {
    if (isXs || isSm) {
      return 80;
    }
    if (isMd) {
      return 100;
    }
    return 120;
  }, [isXs, isSm, isMd]);

  const {
    highAlarm,
    highWarning,
    lowAlarm,
    lowWarning,
  } = thresholds;

  const percentage: number = _.isNil(value) || _.isNil(minValue) || _.isNil(maxValue)
    ? 0
    : currentValuePercentage({
      value,
      minValue,
      maxValue,
    });

  let currentColor: string;

  // Determines the current speedometer color based on calculated percentages
  if (!running || thresholdValidationError) {
    currentColor = theme.color.speedometer.offProgressBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highAlarm) && value >= highAlarm)
      || (!_.isNil(lowAlarm) && value <= lowAlarm)
    )
  ) {
    currentColor = theme.color.speedometer.alarmBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highWarning) && value >= highWarning)
      || (!_.isNil(lowWarning) && value <= lowWarning)
    )
  ) {
    currentColor = theme.color.speedometer.warningBackgroundColor;
  } else {
    currentColor = theme.color.speedometer.normalBackgroundColor;
  }

  // Determine CSS class for gradient styling
  const getThresholdClass = (): string => {
    let thresholdClass: string = 'normal';

    if (!running || thresholdValidationError) {
      thresholdClass = 'off';
    } else {
      const isAlarm = !_.isNil(value) && (
        (!_.isNil(highAlarm) && value >= highAlarm)
        || (!_.isNil(lowAlarm) && value <= lowAlarm)
      );

      const isWarning = !_.isNil(value) && (
        (!_.isNil(highWarning) && value >= highWarning)
        || (!_.isNil(lowWarning) && value <= lowWarning)
      );

      if (isAlarm) {
        thresholdClass = 'alarm';
      } else if (isWarning) {
        thresholdClass = 'warning';
      }
    }

    return thresholdClass;
  };

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  // UseEffect to re-render on language change
  useEffect((): void => {
    // Force update component when language changes
  }, [i18n.language]);

  // Function to render the speedometer data, including the value and unit or error icon
  const speedometerData = ({
    value: dataValue,
    unit: dataUnit,
  }: Pick<SpeedometerProps, 'value' | 'unit'>): React.ReactNode => {
    // If there's a threshold validation error, show error icon
    if (thresholdValidationError) {
      return <ThresholdErrorTooltip errorMessage={thresholdValidationError} />;
    }

    const formattedValue: string = _.isNil(dataValue)
      ? DASHBOARD_NULLISH_TEXT
      : formatNumber(dataValue, decimalPlaces);

    return (
      <div>
        <DataValue>
          <Paragraph
            align={ParagraphAlign.center}
            size={isXs ? ParagraphSize.sm : ParagraphSize.md}
          >
            {formattedValue}
          </Paragraph>
        </DataValue>
        {!_.isNil(dataValue) && (
          <UnitType>
            <Paragraph
              align={ParagraphAlign.center}
              color={theme.color.speedometer.unitTextColor}
              size={isXs ? ParagraphSize.xs : ParagraphSize.md}
            >
              {dataUnit}
            </Paragraph>
          </UnitType>
        )}
      </div>
    );
  };

  return (
    <Container
      data-testid={testId}
      className={className}
    >
      <GradientDefinitions>
        <defs>
          {/* Normal gradient - green */}
          <linearGradient id="speedometer-gradient-normal" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={theme.color.speedometer.normalGradientColor3} />
            <stop offset="10%" stopColor={theme.color.speedometer.normalGradientColor2} />
            <stop offset="15%" stopColor={theme.color.speedometer.normalBackgroundColor} />
            <stop offset="100%" stopColor={theme.color.speedometer.normalBackgroundColor} />
          </linearGradient>

          {/* Warning gradient - yellow */}
          <linearGradient id="speedometer-gradient-warning" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={theme.color.speedometer.warningGradientColor3} />
            <stop offset="10%" stopColor={theme.color.speedometer.warningGradientColor2} />
            <stop offset="15%" stopColor={theme.color.speedometer.warningBackgroundColor} />
            <stop offset="100%" stopColor={theme.color.speedometer.warningBackgroundColor} />
          </linearGradient>

          {/* Alarm gradient - red */}
          <linearGradient id="speedometer-gradient-alarm" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={theme.color.speedometer.alarmGradientColor3} />
            <stop offset="10%" stopColor={theme.color.speedometer.alarmGradientColor2} />
            <stop offset="15%" stopColor={theme.color.speedometer.alarmBackgroundColor} />
            <stop offset="100%" stopColor={theme.color.speedometer.alarmBackgroundColor} />
          </linearGradient>
        </defs>
      </GradientDefinitions>

      <ContentContainer data-testid={testId ? `${testId}-content` : undefined}>
        <MinValueContainer>
          <MinValueTick />
          <MinValueNumber>
            <Paragraph
              align={ParagraphAlign.right}
              size={ParagraphSize.xs}
              color={theme.color.speedometer.ticksValuesTextColor}
            >
              {formatNumber(minValue, decimalPlaces)}
            </Paragraph>
          </MinValueNumber>
        </MinValueContainer>

        <MaxValueContainer>
          <MaxValueTick />
          <MaxValueNumber>
            <Paragraph
              align={ParagraphAlign.left}
              size={ParagraphSize.xs}
              color={theme.color.speedometer.ticksValuesTextColor}
            >
              {formatNumber(maxValue, decimalPlaces)}
            </Paragraph>
          </MaxValueNumber>
        </MaxValueContainer>

        <ProgressSpeedometer
          className={getThresholdClass()}
          data-testid={testId ? `${testId}-progress` : undefined}
        >
          <ProgressAntD
            format={() => speedometerData({ value, unit })}
            gapDegree={100}
            percent={percentage}
            size={progressSize}
            strokeColor={currentColor}
            strokeLinecap="round"
            strokeWidth={3}
            type="dashboard"
          />
        </ProgressSpeedometer>
      </ContentContainer>
      <TitleContainer data-testid={testId ? `${testId}-title` : undefined}>
        <Paragraph
          size={(isXs || isSm) ? ParagraphSize.xs : ParagraphSize.sm}
          weight={ParagraphWeight.medium}
          color={theme.color.speedometer.titleTextColor}
        >
          {title}
        </Paragraph>
      </TitleContainer>
    </Container>
  );
}

export { Speedometer };
